import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Ruler, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ClubFittingGuidePage = () => {
  const navigate = useNavigate();
  const [height, setHeight] = useState("");
  const [wristToFloor, setWristToFloor] = useState("");
  const [result, setResult] = useState(null);

  const calculateFitting = () => {
    if (!height || !wristToFloor) {
      return;
    }

    const heightNum = parseFloat(height);
    const wristNum = parseFloat(wristToFloor);

    // Calculate ratio for club length adjustment
    const ratio = wristNum / heightNum;

    let lengthAdjustment = "";
    let lengthDescription = "";

    // Standard club fitting chart based on wrist-to-floor measurement
    if (ratio < 0.475) {
      lengthAdjustment = "+1.5 til +2.0 tommer";
      lengthDescription = "Lengre enn standard";
    } else if (ratio < 0.485) {
      lengthAdjustment = "+1.0 til +1.5 tommer";
      lengthDescription = "Litt lengre enn standard";
    } else if (ratio < 0.495) {
      lengthAdjustment = "+0.5 til +1.0 tommer";
      lengthDescription = "Lett lengre enn standard";
    } else if (ratio < 0.515) {
      lengthAdjustment = "Standard lengde";
      lengthDescription = "Standard lengde passer perfekt";
    } else if (ratio < 0.525) {
      lengthAdjustment = "-0.5 til -1.0 tommer";
      lengthDescription = "Litt kortere enn standard";
    } else if (ratio < 0.535) {
      lengthAdjustment = "-1.0 til -1.5 tommer";
      lengthDescription = "Kortere enn standard";
    } else {
      lengthAdjustment = "-1.5 til -2.0 tommer";
      lengthDescription = "Mye kortere enn standard";
    }

    // Flex recommendation based on typical patterns
    let flexRecommendation = "";
    let flexDescription = "";

    if (heightNum < 165) {
      flexRecommendation = "Senior (A) eller Ladies (L)";
      flexDescription = "Mykere flex gir bedre ballhastighet og høyde for moderatere svingfart";
    } else if (heightNum < 175) {
      flexRecommendation = "Regular (R)";
      flexDescription = "Passer de fleste golfere med normal svingfart";
    } else if (heightNum < 185) {
      flexRecommendation = "Regular (R) eller Stiff (S)";
      flexDescription = "Avhenger av din svingfart - Regular for de fleste, Stiff hvis du slår langt";
    } else {
      flexRecommendation = "Stiff (S)";
      flexDescription = "Anbefales for høyere spillere med raskere svingfart";
    }

    setResult({
      lengthAdjustment,
      lengthDescription,
      flexRecommendation,
      flexDescription,
      ratio: ratio.toFixed(3),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateFitting();
  };

  const resetForm = () => {
    setHeight("");
    setWristToFloor("");
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => navigate("/")}
              data-testid="logo-home-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_golfbazar/artifacts/d3dy0w6h_Skjermbilde%202025-12-20%20125648.png" 
                alt="GolfMarked Logo" 
                className="h-14 w-auto"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <Ruler className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-3">
            Club Fitting Guide
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Finn riktig lengde og stivhet på dine golfkøller
          </p>
        </div>

        {/* Instructions Card */}
        <Card className="mb-8 border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-900">
              <Info className="w-5 h-5" />
              Slik måler du
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-gray-700">
            <div>
              <strong>1. Høyde:</strong> Mål din høyde i centimeter uten sko.
            </div>
            <div>
              <strong>2. Håndledd til gulv:</strong>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li>Stå rett med armene hengende naturlig ned langs siden</li>
                <li>Ha på deg vanlige sko</li>
                <li>Mål fra håndleddsbenene (der hånden møter armen) rett ned til gulvet</li>
                <li>Målingen gjøres best med en medhjelper</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Input Form */}
        <Card className="mb-8 bg-white shadow-lg">
          <CardHeader>
            <CardTitle>Dine mål</CardTitle>
            <CardDescription>
              Skriv inn målene dine for å få en anbefaling
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="height">Din høyde (cm) *</Label>
                  <Input
                    id="height"
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="f.eks. 175"
                    className="mt-1"
                    min="140"
                    max="220"
                    data-testid="height-input"
                  />
                </div>
                <div>
                  <Label htmlFor="wristToFloor">Håndledd til gulv (cm) *</Label>
                  <Input
                    id="wristToFloor"
                    type="number"
                    value={wristToFloor}
                    onChange={(e) => setWristToFloor(e.target.value)}
                    placeholder="f.eks. 85"
                    className="mt-1"
                    min="60"
                    max="110"
                    data-testid="wrist-to-floor-input"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={!height || !wristToFloor}
                  data-testid="calculate-button"
                >
                  Beregn anbefaling
                </Button>
                {result && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    data-testid="reset-button"
                  >
                    Nullstill
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <div className="space-y-6" data-testid="results-section">
            {/* Length Recommendation */}
            <Card className="bg-white shadow-lg border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="text-emerald-900">📏 Lengde på jern</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="length-result">
                    {result.lengthAdjustment}
                  </p>
                  <p className="text-gray-600 mt-2">{result.lengthDescription}</p>
                </div>
                <Alert>
                  <AlertDescription>
                    Dette er en anbefaling basert på ditt forhold mellom høyde og håndledd-til-gulv mål (ratio: {result.ratio}).
                    Standard jern er vanligvis designet for noen med ratio rundt 0.50.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Flex Recommendation */}
            <Card className="bg-white shadow-lg border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="text-blue-900">💪 Stivhet (Flex)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="flex-result">
                    {result.flexRecommendation}
                  </p>
                  <p className="text-gray-600 mt-2">{result.flexDescription}</p>
                </div>
                <Alert>
                  <AlertDescription>
                    <strong>Flex-guide:</strong> Ladies (L) = Mykest | Senior (A) = Myk | Regular (R) = Normal | 
                    Stiff (S) = Stiv | Extra Stiff (X) = Ekstra stiv
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Additional Tips */}
            <Card className="bg-amber-50 border-amber-200">
              <CardHeader>
                <CardTitle className="text-amber-900">💡 Viktig å huske</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-gray-700">
                <p>• Dette er kun en veiledning - profesjonell club fitting anbefales sterkt</p>
                <p>• Din svingfart og svingteknikk påvirker også valget</p>
                <p>• Prøv alltid køllene før du kjøper hvis mulig</p>
                <p>• Mange golfbutikker tilbyr gratis eller rimelig fitting</p>
              </CardContent>
            </Card>

            {/* CTA to browse listings */}
            <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
              <CardContent className="py-8 text-center">
                <h3 className="text-2xl font-bold mb-3">
                  Klar til å finne dine perfekte jern?
                </h3>
                <p className="mb-6 text-emerald-50">
                  Bla gjennom våre annonser for brukt golfutstyr
                </p>
                <Button
                  onClick={() => navigate("/?category=jern")}
                  variant="secondary"
                  size="lg"
                  className="bg-white text-emerald-700 hover:bg-gray-100"
                  data-testid="browse-irons-button"
                >
                  Se jernsett til salgs
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubFittingGuidePage;
